import { createAsyncThunk } from '@reduxjs/toolkit';
import { ObjectId } from 'app/common.types';
import { AppDispatch, RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import { fetchConversionRateApi } from '../../dashboards/analytics/api';
import { checkSenderIdValidityApi } from '../../franchises/api';
import { FranchiseFeatureType } from '../../franchises/type';
import { getLoyaltyModelApi } from '../../loyalties/api';
import { LoyaltyType } from '../../loyalties/type';
import { patchStoreApi, patchStoreSettingsApi } from '../../stores/api';
import { fetchMinimalStoreListThunk } from '../../stores/ducks/stores.thunk';
import { StoreSettingsDTO } from '../../stores/type';
import {
  deactivateFranchiseApi,
  getFranchiseApi,
  getFranchisePartnerApi,
  getOwnersApi,
  getUberallUsersApi,
  patchFranchisePartnerApi,
  patchLoyaltyTypeApi,
  patchSenderIdApi,
  updateFranchiseFeatureStatusAPI,
} from '../api';
import { adminFranchiseEntitySelector } from './admin.selector';
import {
  actions,
  setAdminFranchiseSenderIdErrorType,
  setAdminFranchiseSenderIdLoading,
  setAdminFranchiseSenderIdValidity,
} from './admin.slice';

export const fetchFranchiseById = (franchiseId: string) => async (dispatch: AppDispatch) => {
  dispatch(actions.setIsAdminFranchiseLoading(true));
  try {
    const franchise = await getFranchiseApi(franchiseId);
    dispatch(actions.getAdminFranchiseSuccess(franchise));
  } catch (_e) {
    dispatch(actions.getAdminFranchiseFailed());
    dispatch(
      showMessage({ message: i18n.t('pages.franchisePanel.errorLoadingFranchise', { ns: 'admin' }), variant: 'error' }),
    );
  } finally {
    dispatch(actions.setIsAdminFranchiseLoading(false));
  }
};

export const checkSenderIdValidity = (senderId: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setAdminFranchiseSenderIdLoading(true));
    await checkSenderIdValidityApi(senderId);
    dispatch(setAdminFranchiseSenderIdValidity(true));
    dispatch(setAdminFranchiseSenderIdErrorType(null));
  } catch (error: any) {
    dispatch(setAdminFranchiseSenderIdValidity(false));
    if (error.response.data.error.status)
      dispatch(setAdminFranchiseSenderIdErrorType(error.response.data.error.status));
  } finally {
    dispatch(setAdminFranchiseSenderIdLoading(false));
  }
};

export const updateFranchiseFeatureStatus = createAsyncThunk(
  'adminApp/franchises/updateFranchiseFeatureStatus',
  async (
    { franchiseId, feature, isEnabled }: { franchiseId: string; feature: FranchiseFeatureType; isEnabled: boolean },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await updateFranchiseFeatureStatusAPI(franchiseId, feature, isEnabled);
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateSuccessfully'), variant: 'success' }));
      return response;
    } catch (e) {
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateFailed'), variant: 'error' }));
      return rejectWithValue(e);
    }
  },
);

export const updateFranchiseStatus = createAsyncThunk(
  'adminApp/franchises/updateFranchiseStatus',
  async (franchiseId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await deactivateFranchiseApi(franchiseId);
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateSuccessfully'), variant: 'success' }));
      return response;
    } catch (e) {
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateFailed'), variant: 'error' }));
      return rejectWithValue(e);
    } finally {
      dispatch(actions.setAdminFranchiseDeactivationDialog({ isOpen: false }));
    }
  },
);

export const fetchFranchisePartner = createAsyncThunk(
  'adminApp/franchises/fetchFranchisePartner',
  async (franchiseId: string, { dispatch, rejectWithValue }) => {
    try {
      return await getFranchisePartnerApi(franchiseId);
    } catch (e) {
      dispatch(
        showMessage({ message: i18n.t('pages.franchisePanel.partner.loadFailed', { ns: 'admin' }), variant: 'error' }),
      );
      return rejectWithValue(e);
    }
  },
);

export const updateFranchisePartner = createAsyncThunk(
  'adminApp/franchises/updateFranchisePartner',
  async ({ franchiseId, isPartner }: { franchiseId: string; isPartner: boolean }, { dispatch, rejectWithValue }) => {
    try {
      const response = await patchFranchisePartnerApi(franchiseId, isPartner);
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateSuccessfully'), variant: 'success' }));
      return response;
    } catch (e) {
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateFailed'), variant: 'error' }));
      return rejectWithValue(e);
    }
  },
);

export const updateFranchiseSenderId =
  (senderId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const franchiseId = adminFranchiseEntitySelector(getState())?.id;
      if (!franchiseId) {
        throw new Error('Franchise not found');
      }
      const data = await patchSenderIdApi(franchiseId, senderId);
      dispatch(actions.updateAdminFranchise(data));
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.updateSenderIdFailed'),
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          variant: 'error',
        }),
      );
      throw error;
    }
  };

export const getAdminLoyaltyModel = (franchiseId: string) => async (dispatch: AppDispatch) => {
  dispatch(actions.setIsAdminLoyaltyModelLoading(true));
  try {
    const loyaltyModel = await getLoyaltyModelApi(franchiseId);
    dispatch(actions.getAdminLoyaltyModelSuccess(loyaltyModel));
  } catch (_e) {
    dispatch(actions.getAdminLoyaltyModelFailed());
    dispatch(
      showMessage({
        message: i18n.t('pages.loyalties.alertMessage.LoadingError'),
        variant: 'error',
      }),
    );
  } finally {
    dispatch(actions.setIsAdminLoyaltyModelLoading(false));
  }
};

export const updateLoyaltyType =
  (loyaltyModelId: string, loyaltyType: LoyaltyType) => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const franchiseId = adminFranchiseEntitySelector(getState())?.id;
      if (!franchiseId) {
        throw new Error('Franchise not found');
      }
      const loyaltyModel = await patchLoyaltyTypeApi(loyaltyModelId, loyaltyType);
      dispatch(actions.getAdminLoyaltyModelSuccess(loyaltyModel));
      dispatch(
        showMessage({
          message: i18n.t('pages.franchisePanel.loyaltyTypeUpdated', { ns: 'admin' }),
          variant: 'success',
        }),
      );
    } catch (_e) {
      dispatch(actions.getAdminLoyaltyModelFailed());
    }
  };

export const fetchConversionRate = (franchiseId: string) => async (dispatch: AppDispatch) => {
  dispatch(actions.setIsAdminConversionRateLoading(true));
  try {
    const conversionRate = await fetchConversionRateApi(franchiseId);
    dispatch(actions.getAdminConversionRateSuccess(conversionRate));
  } catch (_e) {
    dispatch(actions.getAdminConversionRateFailed());
  } finally {
    dispatch(actions.setIsAdminConversionRateLoading(false));
  }
};

export const getOwnersThunk = createAsyncThunk(
  'admin/getOwners',
  async ({ franchiseId }: { franchiseId: ObjectId }, { rejectWithValue }) => {
    try {
      const response = await getOwnersApi(franchiseId);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getUberallUsersThunk = createAsyncThunk(
  'admin/getUberallUsers',
  async ({ franchiseId, query }: { franchiseId: ObjectId; query: string }, { rejectWithValue }) => {
    try {
      const response = await getUberallUsersApi(franchiseId, query);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const adminEditStoreSettingsThunk = createAsyncThunk(
  'storesApp/stores/adminEditStoreSettingsThunk',
  async (
    { franchiseId, storeId, settings }: { franchiseId: string; storeId: string; settings: Partial<StoreSettingsDTO> },
    { dispatch },
  ) => {
    try {
      await patchStoreSettingsApi(storeId, settings);
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateSuccessfully'), variant: 'success' }));
      dispatch(fetchMinimalStoreListThunk(franchiseId));
    } catch (_e: any) {
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateFailed'), variant: 'error' }));
    }
  },
);

export const adminSaveStoreCtNumThunk = createAsyncThunk(
  'storesApp/stores/adminSaveStoreCtNumThunk',
  async ({ storeId, ctNum }: { storeId: string; ctNum: string | undefined }, { dispatch }) => {
    try {
      await patchStoreApi(storeId, { ctNum });
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateSuccessfully'), variant: 'success' }));
    } catch (_e: any) {
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateFailed'), variant: 'error' }));
    }
  },
);

export const adminEditStoreCtNumThunk = createAsyncThunk(
  'storesApp/stores/adminEditStoreCtNumThunk',
  async ({ storeId, ctNum }: { storeId: string; ctNum: string | undefined }, { dispatch }) => {
    try {
      dispatch(actions.setStoreCtNum({ storeId, ctNum }));
    } catch (_e: any) {
      dispatch(showMessage({ message: i18n.t('pages.validation.alert.updateFailed'), variant: 'error' }));
    }
  },
);
